import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { Popover } from 'radix-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatDate } from '@/shared/format/date-format';
import { CalendarHoverCardBody } from './CalendarHoverCard';
import {
  MILESTONE_MARKER_SIZE_PX,
  MILESTONE_SWIMLANE_MARKER_TOP_PX,
  type SwimlaneMilestone,
} from './lane-layer';
import type { RovingItem } from './use-roving-calendar-focus';

/**
 * A cluster of milestones at the same date position.
 */
interface MilestoneCluster {
  readonly leftPercent: number;
  readonly milestones: readonly SwimlaneMilestone[];
}

/**
 * The per-Platform milestone swimlane strip with clustering.
 *
 * Milestones at the same date are clustered into a single marker with a count badge.
 * Clicking a cluster opens a popup list showing all milestones in that cluster.
 * This saves significant vertical space and keeps the swimlane at a fixed minimal height.
 */
/**
 * The milestone's type name, or the generic fallback.
 *
 * The name rides on the marker itself, resolved server-side (R10.10). This
 * previously went through an injected `typeLabelFor` resolver that no caller ever
 * supplied, so every label collapsed to the fallback and no marker named its type
 * — the gap the UI spec recorded. The resolver is gone: there is nothing left to
 * inject, and a prop that silently defaults to "generic word" is how the gap
 * survived unnoticed.
 */
function typeLabel(milestone: SwimlaneMilestone, t: TFunction): string {
  return milestone.milestoneTypeName ?? t('milestone.fallbackLabel');
}

export interface MilestoneSwimlaneProps {
  readonly milestones: readonly SwimlaneMilestone[];
  /** The bar currently highlighted in this lane (hover/focus), or `null`. */
  readonly highlightedBarId: string | null;
  /** Announce which bar the pointer/focus is now wiring to (or `null` to clear). */
  readonly onHighlight: (barId: string | null) => void;
  /** Opens the parent Initiative's Detail_Panel (R10.13). */
  readonly onOpenInitiative: (initiativeId: string) => void;
  /**
   * The roving-tabindex props for a given milestone, resolved from the lane's
   * focus grid by the caller. The marker merges these with its own hover/focus
   * highlight handlers so both the keyboard model and the highlight stay in sync.
   */
  readonly rovingFor: (milestone: SwimlaneMilestone) => RovingItem;
}

export function MilestoneSwimlane({
  milestones,
  highlightedBarId,
  onHighlight,
  onOpenInitiative,
  rovingFor,
}: MilestoneSwimlaneProps): React.JSX.Element | null {
  if (milestones.length === 0) return null;
  
  // Cluster milestones by position (date)
  const clusters = clusterMilestones(milestones);
  
  return (
    <>
      {clusters.map((cluster, clusterIndex) => (
        <ClusterMarker
          key={`cluster-${clusterIndex}-${cluster.leftPercent}`}
          cluster={cluster}
          highlightedBarId={highlightedBarId}
          onHighlight={onHighlight}
          onOpenInitiative={onOpenInitiative}
          rovingFor={rovingFor}
        />
      ))}
    </>
  );
}

/**
 * Cluster milestones by their position (date).
 * Milestones at the same leftPercent are grouped together.
 */
function clusterMilestones(milestones: readonly SwimlaneMilestone[]): readonly MilestoneCluster[] {
  const byPosition = new Map<number, SwimlaneMilestone[]>();
  
  for (const milestone of milestones) {
    const key = Math.round(milestone.leftPercent * 1000) / 1000;
    if (!byPosition.has(key)) {
      byPosition.set(key, []);
    }
    byPosition.get(key)!.push(milestone);
  }
  
  const clusters: MilestoneCluster[] = [];
  for (const [leftPercent, clusterMilestones] of byPosition.entries()) {
    clusters.push({
      leftPercent,
      milestones: clusterMilestones.sort((a, b) => 
        a.parentBarId < b.parentBarId ? -1 : a.parentBarId > b.parentBarId ? 1 : 0
      ),
    });
  }
  
  return clusters.sort((a, b) => a.leftPercent - b.leftPercent);
}

/**
 * Calculate horizontal offset needed to align milestone with its parent bar's visual edge.
 * 
 * Initiative bars have 2px inset on each side for spacing. When a milestone is at the
 * start or end of its parent bar, we need to adjust the position to align with the
 * bar's rendered edge rather than the percentage position.
 */
function calculateHorizontalOffset(milestone: SwimlaneMilestone): string | null {
  const BAR_EDGE_INSET_PX = 2; // Matches InitiativeBar left/right 2px inset
  const tolerance = 0.5; // Tolerance for floating point comparison
  
  const parentRightPercent = milestone.parentLeftPercent + milestone.parentWidthPercent;
  
  // Check if milestone is at the left edge (start) of the parent bar
  const isAtStart = Math.abs(milestone.leftPercent - milestone.parentLeftPercent) < tolerance;
  if (isAtStart) {
    return ` + ${BAR_EDGE_INSET_PX}px`;
  }
  
  // Check if milestone is at the right edge (end) of the parent bar
  const isAtEnd = Math.abs(milestone.leftPercent - parentRightPercent) < tolerance;
  if (isAtEnd) {
    return ` - ${BAR_EDGE_INSET_PX}px`;
  }
  
  // Milestone is in the middle, no adjustment needed
  return null;
}

/** The height of the cluster pill (diamond + count), in px. */
const CLUSTER_PILL_HEIGHT_PX = 18;

/**
 * The diamond glyph of §4.4, reused everywhere a milestone is drawn so the icon
 * vocabulary stays identical between a single marker, a cluster pill and a row in
 * the cluster list.
 */
function MilestoneDiamond({
  completed,
  sizePx = MILESTONE_MARKER_SIZE_PX,
  borderPx = 2,
  emphasised = false,
}: {
  readonly completed: boolean;
  readonly sizePx?: number;
  readonly borderPx?: number;
  readonly emphasised?: boolean;
}): React.JSX.Element {
  return (
    <span
      aria-hidden
      className="block shrink-0 rounded-[1px]"
      style={{
        width: `${String(sizePx)}px`,
        height: `${String(sizePx)}px`,
        transform: emphasised ? 'rotate(45deg) scale(1.25)' : 'rotate(45deg)',
        border: `${String(borderPx)}px solid var(--navy)`,
        background: completed ? 'var(--navy)' : '#ffffff',
        boxShadow: emphasised ? '0 0 0 2px rgba(21, 42, 71, 0.18)' : undefined,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      }}
    />
  );
}

/**
 * A cluster marker: a bare diamond for a single milestone, or a pill carrying the
 * diamond plus the milestone count when several share the date.
 *
 * The cluster's list is rendered through a Radix `Popover.Portal`, so it escapes
 * both the lane track's paint order (the Initiative bars are later siblings and
 * would otherwise cover it) and the calendar card's `overflow-hidden` clip.
 */
function ClusterMarker({
  cluster,
  highlightedBarId,
  onHighlight,
  onOpenInitiative,
  rovingFor,
}: {
  readonly cluster: MilestoneCluster;
  readonly highlightedBarId: string | null;
  readonly onHighlight: (barId: string | null) => void;
  readonly onOpenInitiative: (initiativeId: string) => void;
  readonly rovingFor: (milestone: SwimlaneMilestone) => RovingItem;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const firstMilestone = cluster.milestones[0]!;
  const count = cluster.milestones.length;
  const isCluster = count > 1;

  // The first milestone of the cluster carries the roving-tabindex coordinate, so
  // the strip keeps exactly one focus stop per rendered marker.
  const roving = rovingFor(firstMilestone);

  const isHighlighted = cluster.milestones.some((m) => m.parentBarId === highlightedBarId);

  const horizontalOffset = calculateHorizontalOffset(firstMilestone);
  const leftPosition = horizontalOffset
    ? `calc(${String(cluster.leftPercent)}%${horizontalOffset})`
    : `${String(cluster.leftPercent)}%`;

  // The pill is taller than a bare diamond, so it is raised by half the difference
  // to keep both variants' diamond centres on the same line.
  const topPx = isCluster
    ? MILESTONE_SWIMLANE_MARKER_TOP_PX + MILESTONE_MARKER_SIZE_PX / 2 - CLUSTER_PILL_HEIGHT_PX / 2
    : MILESTONE_SWIMLANE_MARKER_TOP_PX;

  const triggerClassName = cn(
    'block outline-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]',
  );

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    // Enter/Space are the activation keys of the composite widget; for a cluster
    // they toggle the list (Radix handles that on the trigger), for a single
    // milestone the roving controller opens the Detail_Panel.
    if (isCluster && (event.key === 'Enter' || event.key === ' ')) return;
    roving.onKeyDown(event);
  };

  return (
    <div
      className="absolute"
      style={{
        left: leftPosition,
        top: `${String(topPx)}px`,
        transform: 'translateX(-50%)',
        // Above the Initiative bars, which are later siblings in the lane track.
        zIndex: isOpen ? 30 : 20,
      }}
    >
      {isCluster ? (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              ref={roving.ref}
              tabIndex={roving.tabIndex}
              onKeyDown={onTriggerKeyDown}
              onFocus={roving.onFocus}
              aria-label={t('milestone.clusterAccessibleName', {
                count,
                date: formatDate(firstMilestone.plannedDate),
              })}
              className={triggerClassName}
            >
              {/* The §4.4 diamond plus the count, so the icon vocabulary is the
                  same one a single milestone uses (R10.6, R18.8). */}
              <span
                className="flex items-center gap-[4px] rounded-full border pl-[5px] pr-[6px]"
                style={{
                  height: `${String(CLUSTER_PILL_HEIGHT_PX)}px`,
                  background: 'var(--panel)',
                  borderColor: isOpen || isHighlighted ? 'var(--navy)' : 'var(--line)',
                  boxShadow:
                    isOpen || isHighlighted ? '0 0 0 2px rgba(21, 42, 71, 0.18)' : undefined,
                  transition: 'border-color 120ms ease, box-shadow 120ms ease',
                }}
              >
                <MilestoneDiamond completed={firstMilestone.completed} />
                <span className="text-[11px] font-bold leading-none text-[var(--navy)]">
                  {count}
                </span>
              </span>
            </button>
          </Popover.Trigger>
          {/* Portalled so the list is neither painted under the bars nor clipped
              by the calendar card's `overflow-hidden`. */}
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="center"
              sideOffset={6}
              collisionPadding={12}
              className="border-line bg-panel z-50 min-w-[280px] max-w-[360px] rounded-[var(--radius-card)] border p-2 shadow-[var(--shadow-drawer)]"
            >
              <div className="border-line-soft mb-2 flex items-center justify-between gap-2 border-b pb-2">
                <span className="text-txt text-[11px] font-semibold">
                  {t('milestone.clusterTitle', {
                    count,
                    date: formatDate(firstMilestone.plannedDate),
                  })}
                </span>
                <Popover.Close
                  aria-label={t('milestone.closeCluster')}
                  className="text-muted-foreground hover:text-txt text-[16px] leading-none"
                >
                  ×
                </Popover.Close>
              </div>
              <ul className="flex max-h-[300px] flex-col gap-1 overflow-y-auto">
                {cluster.milestones.map((milestone) => (
                  <li key={`${milestone.parentBarId}-${String(milestone.milestoneIndex)}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onOpenInitiative(milestone.parentBarId);
                        setIsOpen(false);
                      }}
                      onPointerEnter={() => onHighlight(milestone.parentBarId)}
                      onPointerLeave={() => onHighlight(null)}
                      className={cn(
                        'w-full rounded-[var(--radius-control)] p-2 text-left text-[11px] transition-colors',
                        'hover:bg-[var(--surface-alt)]',
                        highlightedBarId === milestone.parentBarId && 'bg-[var(--surface-alt)]',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-[3px] block">
                          <MilestoneDiamond
                            completed={milestone.completed}
                            sizePx={8}
                            borderPx={1.5}
                          />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                          <span className="text-txt font-semibold">
                            {typeLabel(milestone, t)}
                          </span>
                          <span className="text-muted-foreground truncate">
                            {milestone.parentTitle}
                          </span>
                          {/* The planned date stands where the Initiative
                              reference used to, so each row is readable on its
                              own without carrying an internal id. */}
                          <span className="text-muted-foreground">
                            {formatDate(milestone.plannedDate)}
                          </span>
                          <span className="text-muted-foreground">
                            {milestone.completed
                              ? t('milestone.completed')
                              : t('milestone.planned')}
                          </span>
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onOpenInitiative(firstMilestone.parentBarId)}
              ref={roving.ref}
              tabIndex={roving.tabIndex}
              onKeyDown={onTriggerKeyDown}
              onPointerEnter={() => onHighlight(firstMilestone.parentBarId)}
              onPointerLeave={() => onHighlight(null)}
              onFocus={() => {
                roving.onFocus();
                onHighlight(firstMilestone.parentBarId);
              }}
              onBlur={() => onHighlight(null)}
              aria-label={t(
                firstMilestone.completed
                  ? 'milestone.swimlaneAccessibleNameCompleted'
                  : 'milestone.swimlaneAccessibleName',
                {
                  label: typeLabel(firstMilestone, t),
                  initiative: firstMilestone.parentTitle,
                  ref: firstMilestone.parentInitiativeRef,
                  date: formatDate(firstMilestone.plannedDate),
                },
              )}
              className={triggerClassName}
            >
              <MilestoneDiamond
                completed={firstMilestone.completed}
                emphasised={isHighlighted}
              />
            </button>
          </TooltipTrigger>
          {/* The `panel` surface plus the shared body: the same card the cluster
              popover uses, so hovering one milestone and opening a cluster of them
              read as the same component. */}
          <TooltipContent side="top" variant="panel" sideOffset={6}>
            <CalendarHoverCardBody
              title={typeLabel(firstMilestone, t)}
              icon={<MilestoneDiamond completed={firstMilestone.completed} sizePx={8} borderPx={1.5} />}
            >
              {/* The same order a cluster-list row uses: the milestone type heads
                  the card, the Initiative it belongs to sits directly beneath, then
                  the planned date and the state. Hovering a single milestone and
                  opening a cluster of them therefore read identically — which is
                  the whole point of sharing one hover-card body (§4.4).

                  The Initiative reference is an internal id and stays off the card;
                  the Detail_Panel still carries it. */}
              <span>{firstMilestone.parentTitle}</span>
              <span>{formatDate(firstMilestone.plannedDate)}</span>
              <span>
                {firstMilestone.completed ? t('milestone.completed') : t('milestone.planned')}
              </span>
            </CalendarHoverCardBody>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
